import * as React from 'react';

import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  Box,
  Button,
  Drawer,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import classNames from 'classnames';

import debounce from 'debounce';
import { ChangeEventValue } from 'google-map-react';

import {
  HOVER_DISTANCE,
  MAP_INITIAL_LAT,
  MAP_INITIAL_LNG,
  MAP_INITIAL_ZOOM,
  MAP_MARGIN,
} from '@kamernet/constants/Search';
import { useIntl } from '@kamernet/core/Intl';
import { CommonStyles } from '@kamernet/styles/CommonStyles';

import { KNGoogleMap } from '@kamernet/modules/GIS/components/KNGoogleMap';

import { ListingsSearchContext } from '../../ListingsSearchContext';
import { areMapCoordsEqual, isMapInInitialState } from '../../utilities';
import { ListingsGrid } from '../ListingsGrid';

import { ClusterMarker } from './ClusterMarker';
import { ListingMarker } from './ListingMarker';
import styles from './MapSearchViewSection.module.css';
import { useMapClusters } from './useMapCluster';

export const MapSearchViewSection = () => {
  const [mobileGrid, setMobileGrid] = React.useState(false);
  const theme = useTheme();
  const isMDUp = useMediaQuery(theme.breakpoints.up('md'));
  const { allFilters, applyNewAllFilters } = ListingsSearchContext.useContext();
  const [showResetButton, setShowResetButton] = React.useState(false);
  const { T } = useIntl();
  const [center, setCenter] = React.useState<{ lat: number; lng: number }>();
  const [zoom, setZoom] = React.useState<number>();
  const { clusters, getClusterZoom } = useMapClusters();

  const [defaultCenter] = React.useState({
    lat: (allFilters.nwlat + allFilters.selat) / 2,
    lng: (allFilters.nwlng + allFilters.selng) / 2,
  });

  const [defaultZoom] = React.useState(allFilters.mapZoom);

  const openMobileGrid = React.useCallback(() => {
    setMobileGrid(true);
  }, []);

  const closeMobileGrid = React.useCallback(() => {
    setMobileGrid(false);
  }, []);

  const applyNewAllFiltersDebounced = React.useMemo(
    () =>
      debounce((mapChange: ChangeEventValue) => {
        if (
          !areMapCoordsEqual(
            mapChange.center.lat,
            (allFilters.nwlat + allFilters.selat) / 2,
          ) ||
          !areMapCoordsEqual(
            mapChange.center.lng,
            (allFilters.nwlng + allFilters.selng) / 2,
          ) ||
          mapChange.zoom !== allFilters.mapZoom
        ) {
          applyNewAllFilters({
            nwlat: mapChange.bounds.nw.lat,
            nwlng: mapChange.bounds.nw.lng,
            selat: mapChange.bounds.se.lat,
            selng: mapChange.bounds.se.lng,
            mapZoom: mapChange.zoom,
            mapMarkerLat: 0,
            mapMarkerLng: 0,
          });
        }
      }, 700),
    [
      allFilters.mapZoom,
      allFilters.nwlat,
      allFilters.nwlng,
      allFilters.selat,
      allFilters.selng,
      applyNewAllFilters,
    ],
  );

  const handleMapChange = React.useCallback(
    async (mapChange: ChangeEventValue) => {
      setCenter({ lat: mapChange.center.lat, lng: mapChange.center.lng });
      setZoom(mapChange.zoom);

      setShowResetButton(
        !isMapInInitialState(
          mapChange.center.lat,
          mapChange.center.lng,
          mapChange.zoom,
        ),
      );

      applyNewAllFiltersDebounced(mapChange);
    },
    [applyNewAllFiltersDebounced],
  );

  const handleResetClick = React.useCallback(() => {
    setZoom(MAP_INITIAL_ZOOM);
    setCenter({
      lat: MAP_INITIAL_LAT,
      lng: MAP_INITIAL_LNG,
    });
    setShowResetButton(false);
  }, []);

  const handleClusterClick = React.useCallback(
    (id: number, coords: number[]) => {
      let [lng, lat] = coords;
      lat = lat || 0;
      lng = lng || 0;
      setCenter({ lat, lng });
      setZoom(getClusterZoom(id) + 1);
    },
    [getClusterZoom],
  );

  const handleMarkerClick = React.useCallback(
    (latitude: number, longitude: number) => {
      if (!isMDUp) {
        openMobileGrid();
      }

      applyNewAllFilters({
        mapMarkerLat: latitude,
        mapMarkerLng: longitude,
      });
    },
    [applyNewAllFilters, isMDUp, openMobileGrid],
  );

  return (
    <div className={styles.container}>
      <Box className={styles.mapRoot}>
        <KNGoogleMap
          defaultCenter={defaultCenter}
          defaultZoom={defaultZoom}
          center={center}
          zoom={zoom}
          options={{
            fullscreenControl: false,
            gestureHandling: isMDUp ? 'greedy' : 'cooperative',
            zoomControl: isMDUp,
          }}
          margin={MAP_MARGIN}
          hoverDistance={HOVER_DISTANCE}
          resetBoundsOnResize
          yesIWantToUseGoogleMapApiInternals
          onChange={handleMapChange}
        >
          {clusters.map(cluster => {
            const [longitude, latitude] = cluster.geometry.coordinates;
            return cluster.properties.cluster ? (
              <ClusterMarker
                key={`cluster-${cluster.id}`}
                id={parseInt(cluster.id ? cluster.id.toString() : '0', 10)}
                lat={latitude || 0}
                lng={longitude || 0}
                listings={cluster.properties.listings}
                onClick={handleClusterClick}
              />
            ) : (
              <ListingMarker
                key={`cluster-${cluster.id}`}
                lat={latitude || 0}
                lng={longitude || 0}
                listings={cluster.properties.listings}
                isSelected={
                  latitude === allFilters.mapMarkerLat &&
                  longitude === allFilters.mapMarkerLng
                }
                onClick={handleMarkerClick}
              />
            );
          })}
        </KNGoogleMap>

        <div className={styles.mapActions}>
          {showResetButton && (
            <div
              className={classNames(styles.mapReset, CommonStyles.displayFlex)}
            >
              <Button
                startIcon={<RestartAltIcon />}
                variant="contained"
                size="small"
                color="tertiary"
                onClick={handleResetClick}
              >
                <Typography variant="body3">
                  {T('search_page.map.reset_map')}
                </Typography>
              </Button>
            </div>
          )}
        </div>
      </Box>
      {isMDUp ? (
        <div className={styles.mapGridViewContainer}>
          <ListingsGrid className={styles.mapGridView} />
        </div>
      ) : (
        <Drawer
          classes={{ paperAnchorBottom: styles.bottomDrawer }}
          open={mobileGrid}
          anchor="bottom"
          aria-labelledby="Listing"
          aria-describedby="Listing"
          onClose={closeMobileGrid}
        >
          <ListingsGrid />
        </Drawer>
      )}
    </div>
  );
};
